import { ClientContext } from "ClientProvider";
import DivLoader from "components/divloader/DivLoader";
import DateRange from "components/fields/DashboardDateRange";
import { MultiSelect } from "react-multi-select-component";
import React , { useEffect, useState, useContext } from "react";
import moment from "moment";
import {getClientDetails, getOrganizationDetails } from "api/graphQL_dashboard";
import { getSingleRole } from "api/graphQL_role";
import { getId } from "utils/auth";
import { FiSearch } from "react-icons/fi";
import {getSettlementSummary} from "api/settlement"
import {createColumnHelper,flexRender,getCoreRowModel,getSortedRowModel,SortingState,useReactTable} from "@tanstack/react-table";
import {BsArrowDown,BsArrowUp} from "react-icons/bs";
import {RiArrowUpDownFill} from "react-icons/ri";
import ShortTruncateCopy from "components/common/ShortTruncateCopy";
import { getImage } from "utils/commonFunction";
import visa from "assets/svg/card_type/visa.svg";
import MasterCard from "assets/svg/card_type/mastercard.svg";
import Discover from "assets/svg/card_type/discover.svg";
import Amex from "assets/svg/card_type/amex.svg";
import MaestroCard from "assets/svg/card_type/maestro.svg";
import DinersClub from "assets/svg/card_type/dinersclub.svg";
import JCB from "assets/svg/card_type/jcb.svg";
import UnionPay from "assets/svg/card_type/unionpay.svg";

const settlementColumnHelper=createColumnHelper<any>();

const CARD_SVGS: Record<string, string> = {
  VISA: visa, MasterCard, Discover, Amex, MaestroCard, DinersClub, JCB, UnionPay,
};

const getCardSVG = (type: string) => {
  const src = CARD_SVGS[type];
  if (!src) return <span className="txn-muted">—</span>;
  return <img style={{ maxWidth: "56px", height: "auto" }} src={src} alt={type} title={type} />;
};

const TIME_FRAMES = ["today", "yesterday", "weekly", "monthly", "yearly", "all"];

const SettlementSummary = () => {

    const { singleRoleData, setSingleRoleData, settlementSummaryData, setSettlementSummaryData } = useContext(ClientContext);
    const [timeFrameTop, setTimeFrameTop] = useState<any>("today");
    const [loadData,setLoadData] = useState(false);
    const [orgDetails, setOrgDetails] = useState<any>([]);
    const [selectOrg, setSelectOrg] = useState<any>([]);
    const [clientDetails, setClientDetails] = useState<any>([]);
    const [selectClient, setSelectClientDetails] = useState<any>([]);
    const [datesTop, setDatesTop] = useState<[Date | null, Date | null]>();
    const [dateRangeKey, setDateRangeKey] = useState(0);
    const [hasFetchedData, setHasFetchedData] = useState(false);
    const [settlementSummary,setSettlementSummary] = useState<any>({});
    const [settlementSorting,setSettlementSorting]=useState<SortingState>([]);
    const settlementPayments=settlementSummary?.payments||[];
    const name_permission = singleRoleData?.[0]?.settlement?.value?.show_settlement_gateway_name;

    const settlementColumns=[
        settlementColumnHelper.accessor("created_at", {
            id: "created_at",
            header: () => <span className="st-th">Date</span>,
            cell: (info) => <span className="st-amount">{info.getValue().replace("T"," ")}</span>,
            sortingFn: (a, b) =>
                new Date(a.original.created_at).getTime() - new Date(b.original.created_at).getTime(),
            enableSorting: true,
        }),
        settlementColumnHelper.accessor("et_id",{
            id:"et_id",
            header:()=><span className="st-th">Transaction ID</span>,
            cell:(info:any)=>(
                   <ShortTruncateCopy info={info?.getValue()} showCopy={true} />
            ),
            sortingFn:(a,b)=>String(a.original.et_id||"").localeCompare(String(b.original.et_id||""))
        }),
        settlementColumnHelper.accessor("id",{
            id:"id",
            header:()=><span className="st-th">Internal ID</span>,
            cell:(info:any)=>(
                <ShortTruncateCopy info={info?.getValue()} showCopy={true} />
            ),
            sortingFn:(a,b)=>String(a.original.id||"").localeCompare(String(b.original.id||""))
        }),
        settlementColumnHelper.accessor("amount",{
            id:"amount",
            header:()=><span className="st-th">Amount</span>,
            cell:(info:any)=>(
                <div className="st-amount-cell">
                    <span className="st-amount">{Number(info.getValue()||0).toFixed(2)} {info.row.original?.currency?.toUpperCase()}</span>
                    {info?.row?.original?.meta_info?.binInformation?.card_type && (
                        <span className="txn-card-type">
                            {getCardSVG(info?.row?.original?.meta_info?.binInformation?.card_type)}
                        </span>
                    )}
                </div>
            ),
            sortingFn:(a,b)=>(Number(a.original.amount)||0)-(Number(b.original.amount)||0)
        }),
        settlementColumnHelper.accessor("gateway_id", {
            id: "gateway_id",
            header: () => <span className="st-th">Gateway {name_permission ? "Name" : "Hash ID"}</span>,
            cell: (info) => {
                const gName = info?.cell?.row?.original?.gateway_name;
                return name_permission ? (
                    <div className="txn-gateway-cell">
                    <img
                        style={{ height: "auto", width: "16px" }}
                        src={getImage(gName)}
                        alt={gName}
                    />
                    <span className="txn-gateway-name">{gName}</span>
                    </div>
                ) : (
                    <ShortTruncateCopy
                    info={info?.cell?.row?.original?.gateway_id || "—"}
                    showCopy={true}
                    />
                );
            },
            sortingFn: (rowA, rowB) => {
                const a = rowA.original.gateway_id || "";
                const b = rowB.original.gateway_id || "";
                const result = a.localeCompare(b);
                if (result !== 0) return result;
                return String(rowA.original.id).localeCompare(String(rowB.original.id));
            },
        }),
        settlementColumnHelper.accessor("reserve",{
            id:"reserve",
            header:()=><span className="st-th">Reserve</span>,
            cell:(info:any)=>(
                <span className="st-reserve">{Number(info.getValue()||0).toFixed(2)} {info.row.original?.currency?.toUpperCase()}</span>
            ),
            sortingFn:(a,b)=>(Number(a.original.reserve)||0)-(Number(b.original.reserve)||0)
        }),
        // settlementColumnHelper.accessor("reserve_release_amount",{
        //     id:"reserve_release_amount",
        //     header:()=><span className="st-th">Release Amount</span>,
        //     cell:(info:any)=>(
        //         <span className="st-release-amount">{Number(info.getValue()||0).toFixed(2)} {info.row.original?.currency?.toUpperCase()}</span>
        //     ),
        //     sortingFn:(a,b)=>(Number(a.original.reserve_release_amount)||0)-(Number(b.original.reserve_release_amount)||0)
        // }),
        // settlementColumnHelper.accessor("reserve_release_date",{
        //     id:"reserve_release_date",
        //     header:()=><span className="st-th">Release Date</span>,
        //     cell:(info:any)=>(
        //         <span className="st-date">{info.getValue()?new Date(info.getValue()).toLocaleDateString("en-CA"):"—"}</span>
        //     ),
        //     sortingFn:(a,b)=>new Date(a.original.reserve_release_date||0).getTime()-new Date(b.original.reserve_release_date||0).getTime()
        // }),
        // settlementColumnHelper.accessor("is_released",{
        //     id:"is_released",
        //     header:()=><span className="st-th">Released</span>,
        //     cell:(info:any)=>info.getValue()==="yes"?(
        //         <span className="st-release-badge st-release-yes">
        //             <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
        //             Yes
        //         </span>
        //     ):(
        //         <span className="st-release-badge st-release-no">
        //             <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        //             No
        //         </span>
        //     ),
        //     sortingFn:(a,b)=>(a.original.is_released==="yes"?1:0)-(b.original.is_released==="yes"?1:0)
        // }),
        settlementColumnHelper.accessor("status",{
            id:"status",
            header:()=><span className="st-th">Status</span>,
            cell:(info:any)=>(
                <span className={`st-status st-status-${String(info.getValue()||"").toLowerCase()}`}>
                    <span className="st-status-dot"/>
                    {info.getValue()||"—"}
                </span>
            ),
            sortingFn:(a,b)=>String(a.original.status||"").localeCompare(String(b.original.status||""))
        })
    ];
    const settlementTable=useReactTable({
        data:settlementPayments,
        columns:settlementColumns,
        state:{sorting:settlementSorting},
        onSortingChange:setSettlementSorting,
        getCoreRowModel:getCoreRowModel(),
        getSortedRowModel:getSortedRowModel()
    });

    const handleDateRangeChange = (r: [Date | null, Date | null]) => {
        if (r[0] && r[1]) {
            setDatesTop([
                new Date(moment(r[0]).format("YYYY-MM-DD")),
                new Date(moment(r[1]).format("YYYY-MM-DD"))
            ]);
            setTimeFrameTop(null)
        } else {
            setDatesTop(null);
        }
    };

    const fetchSingleRole = () => {
        getSingleRole(getId()).then((d) => setSingleRoleData(d ?? [])).catch(() => {});
    };

    const fetchOrgDetails = () => {
        getOrganizationDetails().then((d) => {
            const m = d?.map((x: any) => ({ ...x?.organization, label: x?.organization?.name, value: x?.organization?.id }));
            setOrgDetails(m);
        }).catch(() => {});
    };

    const fetchClientDetails = () => {
        if (!selectOrg || selectOrg.length === 0) {
            setClientDetails([]);
            setSelectClientDetails([]);
            return;
        }
        const orgIds = selectOrg.map((org: any) => org.id).join(",");
        getClientDetails({ org_ids: orgIds })
            .then((d) => {
                const m = d?.map((x: any) => ({
                    ...x,
                    label: x?.name,
                    value: x?.id
                }));

                setClientDetails(m || []);
            })
            .catch(() => {
                setClientDetails([]);
            });
    };

    const formatAmount = (value: any) => {
        return `$${Number(value || 0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
    };

    const handleFetch = ( ) => {
        setLoadData(true);
        setHasFetchedData(true);

        const start_date =
            datesTop?.[0]
                ? `${datesTop[0].toISOString().substring(0, 10)}`
                : null;

            const end_date =
            datesTop?.[1]
                ? `${datesTop[1].toISOString().substring(0, 10)}`
                : null;

        const data = {
            "org_ids": selectOrg?.length > 0 ? selectOrg?.map((m: any) => m?.id)?.toString() : "",
            "client_ids": selectClient?.length > 0 ? selectClient?.map((m: any) => m?.id)?.toString() : "",
            "start_date" : start_date,
            "end_date" : end_date,
            "time_frame" : timeFrameTop,
            "is_live":settlementSummaryData
        }

        getSettlementSummary(data)
        .then((d) => setSettlementSummary(d || {}))
        .catch(() => setSettlementSummary({}))
        .finally(() => setLoadData(false));

    }

    useEffect(() => {
        setClientDetails([]);
        setSelectClientDetails([]);
        fetchClientDetails();
    }, [selectOrg]);

    useEffect(() => {
        fetchSingleRole();
        fetchOrgDetails();
    }, []);

    return (
        <div className="db-root">
            <div className="db-filter-card">
                <div className="db-toprow">
                    <div className="db-page-title">
                        <div className="db-page-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                            <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
                            </svg>
                        </div>
                        <div>
                            <h1 className="db-heading">Settlement Summary</h1>
                            <p className="db-subheading">Manage settlement summary and settlements details</p>
                        </div>
                    </div>
                </div>
                <div className="db-divider" />
                <div className="db-filter-grid">
                    <div className="db-filter-group">
                    <label className="db-filter-label">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                        Organization
                    </label>
                    <MultiSelect options={orgDetails} value={selectOrg} onChange={setSelectOrg} labelledBy="Organization(s)" isCreatable={false}
                        className="db-ms"
                        overrideStrings={{ noOptions: "No organizations found" }}
                        valueRenderer={(s, o) => s.length === 0 ? "Select organizations" : s.length === o.length ? "All organizations" : s.map(x => x.label).join(", ")}
                    />
                    </div>
                    <div className="db-filter-group">
                    <label className="db-filter-label">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                        Client
                    </label>
                    <MultiSelect options={clientDetails} value={selectClient} onChange={setSelectClientDetails} labelledBy="Client(s)" isCreatable={false}
                        className="db-ms"
                        overrideStrings={{ noOptions: "No clients found" }}
                        valueRenderer={(s, o) => s.length === 0 ? "Select clients" : s.length === o.length ? "All clients" : s.map(x => x.label).join(", ")}
                    />
                    </div>
                    <div className="db-filter-group">
                    <label className="db-filter-label">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                        Custom Date Range
                    </label>
                    <div className="db-daterange">
                        <DateRange key={dateRangeKey} onDateChange={handleDateRangeChange} />
                    </div>
                    </div>
                </div>
        
                <div className="db-divider" />
        
                <div className="db-tf-row">
                    <div className="flex gap-[15px] flex-2">
                        <span className="db-filter-label" style={{ marginBottom: 0, flexShrink: 0 }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        Time Frame
                        </span>
                        <div className="db-tf-pills">
                            {TIME_FRAMES.map((t) => (
                                <button key={t} className={`db-pill${timeFrameTop === t ? " db-pill--active" : ""}`} onClick={() => { setTimeFrameTop(t); setDatesTop(null); setDateRangeKey(prev => prev + 1); }}>
                                {t.charAt(0).toUpperCase() + t.slice(1)}
                                </button>
                            ))}

                            {datesTop?.[0] && datesTop?.[1] && (
                                <button type="button" className="db-pill db-pill--custom" disabled>
                                    Custom Date
                                </button>
                            )}
                        </div>
                        <label className="db-mode-toggle">
                            <input type="checkbox" className="db-toggle-inp" checked={!!settlementSummaryData} onChange={() => setSettlementSummaryData(!settlementSummaryData)} />
                            <div className="db-toggle-track">
                            <div className="db-toggle-thumb" />
                            </div>
                            <div className="db-mode-labels">
                            <span className={`db-mode-lbl${!settlementSummaryData ? " db-mode-lbl--active-test" : ""}`}>Test</span>
                            <span className="db-mode-sep">/</span>
                            <span className={`db-mode-lbl${settlementSummaryData ? " db-mode-lbl--active-live" : ""}`}>Live</span>
                            </div>
                        </label>
                    </div>
                    <div className="flex flex-1">
                        <button className="org-add-btn ml-auto" onClick={handleFetch} >
                            {!loadData ? (
                                <>
                                    <FiSearch className="ct-search-icon" />
                                    View Settlement Data
                                </>
                            ):(
                                <>
                                    <span className="wz-spinner" />
                                    Fetching Data....
                                </>
                            )}
                            
                        </button>
                    </div>
                </div>
            </div>
            {hasFetchedData && loadData && (
                <div className="db-loader-wrap">
                    <span className="wz-spinner-load" /> Fetching Data...
                </div>
            )}
            {hasFetchedData && !loadData && (
                <div className="db-stats-section">
                    <div className="db-stats-header">
                        <div>
                            <h2>Settlement Overview</h2>
                            <p>Financial performance and transaction breakdown</p>
                        </div>
                        <div className="db-stats-live">
                            <span />
                            {settlementSummaryData ? "Live Transactions" : "Test Transactions"}
                        </div>
                    </div>
                    <div className="db-main-stats">
                        <div className="db-stat-card">
                            <div className="db-stat-top">
                                <div className="db-stat-icon db-stat-blue">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18"/><path d="M7 16l4-5 3 3 5-7"/></svg>
                                </div>
                                <span className="db-stat-badge">Transactions</span>
                            </div>
                            <div className="db-stat-value">{settlementSummary?.amount_stats?.total_transactions || 0}</div>
                            <div className="db-stat-label">Total Transactions</div>
                        </div>
                        <div className="db-stat-card">
                            <div className="db-stat-top">
                                <div className="db-stat-icon db-stat-green">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><path d="M15 8.5c-.7-.7-1.7-1-3-1-1.7 0-3 .8-3 2s1.3 2 3 2 3 .8 3 2-1.3 2-3 2c-1.3 0-2.3-.3-3-1"/></svg>
                                </div>
                                <span className="db-stat-badge db-badge-green">Volume</span>
                            </div>
                            <div className="db-stat-value">{formatAmount(settlementSummary?.amount_stats?.total_amount)}</div>
                            <div className="db-stat-label">Total Transaction Amount</div>
                        </div>
                        <div className="db-stat-card">
                            <div className="db-stat-top">
                                <div className="db-stat-icon db-stat-purple">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z"/><path d="M8.5 12l2.2 2.2 4.8-5"/></svg>
                                </div>
                                <span className="db-stat-badge db-badge-purple">Reserve</span>
                            </div>
                            <div className="db-stat-value">{formatAmount(settlementSummary?.amount_stats?.total_reserve)}</div>
                            <div className="db-stat-label">Total Reserve Amount</div>
                        </div>
                        <div className="db-stat-card">
                            <div className="db-stat-top">
                                <div className="db-stat-icon db-stat-orange">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5"/><circle cx="12" cy="12" r="9"/></svg>
                                </div>
                                <span className="db-stat-badge db-badge-orange">Approved</span>
                            </div>
                            <div className="db-stat-value">{formatAmount(settlementSummary?.amount_stats?.amount_approved)}</div>
                            <div className="db-stat-label">
                                {settlementSummary?.amount_stats?.transactions_approved || 0} approved transactions
                            </div>
                        </div>
                    </div>
                    <div className="db-status-card">
                        <div className="db-status-heading">
                            <div>
                                <h3>Transaction Status</h3>
                                <p>Breakdown of settlement transaction performance</p>
                            </div>
                            <div className="db-total-volume">
                                <span>Total</span>
                                <strong>{settlementSummary?.amount_stats?.total_transactions || 0}</strong>
                            </div>
                        </div>
                        <div className="db-status-grid">
                            <div className="db-status-item">
                                <div className="db-status-left">
                                    <span className="db-status-dot db-dot-approved" />
                                    <div><strong>Approved</strong><small>Successful transactions</small></div>
                                </div>
                                <div className="db-status-right">
                                    <strong>{settlementSummary?.amount_stats?.transactions_approved || 0}</strong>
                                    <span>{formatAmount(settlementSummary?.amount_stats?.amount_approved)}</span>
                                </div>
                            </div>
                            <div className="db-status-item">
                                <div className="db-status-left">
                                    <span className="db-status-dot db-dot-declined" />
                                    <div><strong>Declined</strong><small>Declined transactions</small></div>
                                </div>
                                <div className="db-status-right">
                                    <strong>{settlementSummary?.amount_stats?.transactions_declined || 0}</strong>
                                    <span>{formatAmount(settlementSummary?.amount_stats?.amount_declined)}</span>
                                </div>
                            </div>
                            <div className="db-status-item">
                                <div className="db-status-left">
                                    <span className="db-status-dot db-dot-refunded" />
                                    <div><strong>Refunded</strong><small>Refunded transactions</small></div>
                                </div>
                                <div className="db-status-right">
                                    <strong>{settlementSummary?.amount_stats?.transactions_refunded || 0}</strong>
                                    <span>{formatAmount(settlementSummary?.amount_stats?.amount_refunded)}</span>
                                </div>
                            </div>
                            <div className="db-status-item">
                                <div className="db-status-left">
                                    <span className="db-status-dot db-dot-errored" />
                                    <div><strong>Errored</strong><small>Processing errors</small></div>
                                </div>
                                <div className="db-status-right">
                                    <strong>{settlementSummary?.amount_stats?.transactions_errored || 0}</strong>
                                    <span>{formatAmount(settlementSummary?.amount_stats?.amount_errored)}</span>
                                </div>
                            </div>
                            <div className="db-status-item">
                                <div className="db-status-left">
                                    <span className="db-status-dot db-dot-failed" />
                                    <div><strong>Failed</strong><small>Failed transactions</small></div>
                                </div>
                                <div className="db-status-right">
                                    <strong>{settlementSummary?.amount_stats?.transactions_failed || 0}</strong>
                                    <span>{formatAmount(settlementSummary?.amount_stats?.amount_failed)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="db-status-card">
                        <div className="db-status-heading">
                            <div>
                                <h3>Transaction Charges Summary</h3>
                                <p>Breakdown of charges for settlement's transactions</p>
                            </div>
                            <div className="db-total-volume">
                                <span>Total</span>
                                <strong>{settlementSummary?.amount_stats?.total_charges || 0}</strong>
                            </div>
                        </div>
                        <div className="db-charges-grid">
                            <div className="db-status-item">
                                <div className="db-status-left">
                                    <span className="db-status-dot db-dot-approved" />
                                    <div><strong>Transaction Fees (%)</strong></div>
                                </div>
                                <div className="db-status-right">
                                    <strong>{formatAmount(settlementSummary?.amount_stats?.transaction_fee)}</strong>
                                </div>
                            </div>
                            <div className="db-status-item">
                                <div className="db-status-left">
                                    <span className="db-status-dot db-dot-declined" />
                                    <div><strong>Transaction Fees (Fixed)</strong></div>
                                </div>
                                <div className="db-status-right">
                                    <strong>{formatAmount(settlementSummary?.amount_stats?.fixed_transaction_fee)}</strong>
                                </div>
                            </div>
                            <div className="db-status-item">
                                <div className="db-status-left">
                                    <span className="db-status-dot db-dot-refunded" />
                                    <div><strong>Declined  Fees (% + Fixed Fee)</strong></div>
                                </div>
                                <div className="db-status-right">
                                    <strong>{formatAmount(settlementSummary?.amount_stats?.declined_transaction_fee)}</strong>
                                </div>
                            </div>
                        </div>
                        <div className="db-charges-grid">
                            <div className="db-status-item">
                                <div className="db-status-left">
                                    <span className="db-status-dot db-dot-errored" />
                                    <div><strong>Chargeback Fess (% + Fixed Fee)</strong></div>
                                </div>
                                <div className="db-status-right">
                                    <strong>{formatAmount(settlementSummary?.amount_stats?.chargeback_fee)}</strong>
                                </div>
                            </div>
                            <div className="db-status-item">
                                <div className="db-status-left">
                                    <span className="db-status-dot db-dot-failed" />
                                    <div><strong>Refund Fess (% + Fixed Fee)</strong></div>
                                </div>
                                <div className="db-status-right">
                                    <strong>{formatAmount(settlementSummary?.amount_stats?.refund_fee)}</strong>
                                </div>
                            </div>
                            <div className="db-status-item">
                                <div className="db-status-left">
                                    <span className="db-status-dot db-dot-declined" />
                                    <div><strong>Disapute Fees (% + Fixed Fee)</strong></div>
                                </div>
                                <div className="db-status-right">
                                    <strong>{formatAmount(settlementSummary?.amount_stats?.dispute_fee)}</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="db-status-card">
                        <div className="db-status-heading">
                            <div>
                                <h3>Transaction Reserve Summary</h3>
                                <p>Breakdown of reserve for settlement's transactions</p>
                            </div>
                        </div>
                        <div className="db-status-grid">
                            <div className="db-status-item">
                                <div className="db-status-left">
                                    <span className="db-status-dot db-dot-approved" />
                                    <div><strong>Balance Brought Forward</strong></div>
                                </div>
                                <div className="db-status-right">
                                    <strong>{formatAmount(settlementSummary?.amount_stats?.reserve_balance_brought_forward)}</strong>
                                </div>
                            </div>
                            <div className="db-status-item">
                                <div className="db-status-left">
                                    <span className="db-status-dot db-dot-declined" />
                                    <div><strong>Current Period Reserve</strong></div>
                                </div>
                                <div className="db-status-right">
                                    <strong>{formatAmount(settlementSummary?.amount_stats?.total_reserve)}</strong>
                                </div>
                            </div>
                            <div className="db-status-item">
                                <div className="db-status-left">
                                    <span className="db-status-dot db-dot-refunded" />
                                    <div><strong>Current Period Reserve Release</strong></div>
                                </div>
                                <div className="db-status-right">
                                    <strong>{formatAmount(settlementSummary?.amount_stats?.reserve_release)}</strong>
                                </div>
                            </div>
                            <div className="db-status-item">
                                <div className="db-status-left">
                                    <span className="db-status-dot db-dot-errored" />
                                    <div><strong>Net Rolling Reserve</strong></div>
                                </div>
                                <div className="db-status-right">
                                    <strong>{formatAmount(settlementSummary?.amount_stats?.net_rolling_reserve)}</strong>
                                </div>
                            </div>
                            <div className="db-status-item">
                                <div className="db-status-left">
                                    <span className="db-status-dot db-dot-failed" />
                                    <div><strong>Balance Carried Forward</strong></div>
                                </div>
                                <div className="db-status-right">
                                    <strong>{formatAmount(settlementSummary?.amount_stats?.reserve_balance_carried_forward)}</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {hasFetchedData && !loadData && (
                <div className="db-payout-card">
                    <div className="db-payout-header">
                        <div className="db-payout-title-wrap">
                            <div className="db-payout-icon">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M12 2v20"/>
                                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H7"/>
                                </svg>
                            </div>
                            <div>
                                <h3>Final Payout Summary</h3>
                                <p>Settlement calculation after deductions, charges and reserve adjustments</p>
                            </div>
                        </div>
                        <div className="db-payout-badge">
                            <span className="db-payout-badge-dot" />
                            Final Settlement
                        </div>
                    </div>

                    <div className="db-payout-body">
                        <div className="db-payout-calculation">
                            <div className="db-payout-row">
                                <div className="db-payout-label">
                                    <span className="db-payout-row-icon db-payout-positive">
                                        +
                                    </span>
                                    <div>
                                        <strong>Gross Volume</strong>
                                        <small>Approved transaction volume</small>
                                    </div>
                                </div>
                                <span className="db-payout-value db-value-positive">
                                    {formatAmount(settlementSummary?.amount_stats?.gross_volume)}
                                </span>
                            </div>

                            <div className="db-payout-row">
                                <div className="db-payout-label">
                                    <span className="db-payout-row-icon db-payout-negative">
                                        −
                                    </span>
                                    <div>
                                        <strong>Total Deduction</strong>
                                        <small>Refunds & Chargebacks</small>
                                    </div>
                                </div>
                                <span className="db-payout-value db-value-negative">
                                    − {formatAmount(settlementSummary?.amount_stats?.total_deduction)}
                                </span>
                            </div>

                            <div className="db-payout-row">
                                <div className="db-payout-label">
                                    <span className="db-payout-row-icon db-payout-negative">
                                        −
                                    </span>
                                    <div>
                                        <strong>Total Charges</strong>
                                        <small>Transaction & applicable fees</small>
                                    </div>
                                </div>
                                <span className="db-payout-value db-value-negative">
                                    − {formatAmount(settlementSummary?.amount_stats?.total_charges)}
                                </span>
                            </div>

                            <div className="db-payout-equation">
                                <div className="db-payout-equation-line" />
                                <div className="db-payout-subtotal">
                                    <div>
                                        <span>Payout Before Net Rolling Reserve</span>
                                        <small>Gross Volume − Deductions − Charges</small>
                                    </div>
                                    <strong>
                                        {formatAmount(settlementSummary?.amount_stats?.payout_before_net_rolling_reserve)}
                                    </strong>
                                </div>
                            </div>

                            <div className="db-payout-row db-payout-reserve-row">
                                <div className="db-payout-label">
                                    <span className="db-payout-row-icon db-payout-reserve">
                                        −
                                    </span>
                                    <div>
                                        <strong>Reserve Balance Carried Forward</strong>
                                        <small>Net rolling reserve adjustment</small>
                                    </div>
                                </div>
                                <span className="db-payout-value db-value-reserve">
                                    − {formatAmount(settlementSummary?.amount_stats?.reserve_balance_carried_forward)}
                                </span>
                            </div>

                            <div className="db-payout-final">
                                <div className="db-payout-final-left">
                                    <div className="db-payout-final-icon">
                                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M20 6L9 17l-5-5"/>
                                        </svg>
                                    </div>
                                    <div>
                                        <span>Net Payout</span>
                                        <small>Final amount payable</small>
                                    </div>
                                </div>
                                <strong>
                                    {formatAmount(settlementSummary?.amount_stats?.net_payout)}
                                </strong>
                            </div>
                        </div>

                        <div className="db-payout-formula">
                            <div className="db-formula-title">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 6h16"/>
                                    <path d="M4 12h16"/>
                                    <path d="M4 18h10"/>
                                </svg>
                                Calculation
                            </div>

                            <div className="db-formula-content">
                                <span>Gross Volume</span>
                                <b>−</b>
                                <span>Deductions</span>
                                <b>−</b>
                                <span>Charges</span>
                                <b>=</b>
                                <strong>Payout Before Reserve</strong>
                                <b>−</b>
                                <span>Reserve</span>
                                <b>=</b>
                                <strong className="db-formula-final">Net Payout</strong>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {hasFetchedData && !loadData &&(
                <div className="st-card">
                    <div className="st-header">
                        <div className="st-header-left">
                            <div className="st-header-icon">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="4" width="18" height="16" rx="2"/>
                                    <path d="M3 10h18"/>
                                    <path d="M7 15h2"/>
                                    <path d="M13 15h4"/>
                                </svg>
                            </div>
                            <div>
                                <h3 className="st-title">Settlement Transactions</h3>
                                <p className="st-subtitle">
                                    {settlementPayments.length>0?`${settlementPayments.length.toLocaleString()} payment${settlementPayments.length!==1?"s":""} found`:"No payments found"}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="st-table-wrap">
                        <table className="st-table">
                            <thead>
                                {settlementTable.getHeaderGroups().map(hg=>(
                                    <tr key={hg.id}>
                                        {hg.headers.map(h=>(
                                            <th key={h.id} onClick={h.column.getToggleSortingHandler()} className={`st-thead-th${h.column.getCanSort()?" st-thead-th--sort":""}`}>
                                                <div className="st-th-inner">
                                                    {flexRender(h.column.columnDef.header,h.getContext())}
                                                    {h.column.getCanSort()&&(
                                                        <span className="st-sort-icon">
                                                            {h.column.getIsSorted()==="asc"?<BsArrowUp size={11}/>:h.column.getIsSorted()==="desc"?<BsArrowDown size={11}/>:<RiArrowUpDownFill size={11}/>}
                                                        </span>
                                                    )}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                {settlementTable.getRowModel().rows.length>0?(
                                    settlementTable.getRowModel().rows.map((row,index)=>(
                                        <tr key={row.id} className={`st-tbody-tr${index%2!==0?" st-tbody-tr--alt":""}`}>
                                            {row.getVisibleCells().map(cell=>(
                                                <td key={cell.id} className="st-tbody-td">
                                                    {flexRender(cell.column.columnDef.cell,cell.getContext())}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                ):(
                                    <tr>
                                        <td colSpan={settlementColumns.length}>
                                            <div className="st-empty">
                                                <div className="st-empty-icon">
                                                    <svg width="25" height="25" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                        <rect x="3" y="4" width="18" height="16" rx="2"/>
                                                        <path d="M3 10h18"/>
                                                    </svg>
                                                </div>
                                                <p className="st-empty-title">No settlement payments found</p>
                                                <p className="st-empty-sub">Try adjusting your filters or date range</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            {!hasFetchedData && (
                <div className="db-empty-state">
                    <div className="db-empty-icon">
                        <svg
                            width="32"
                            height="32"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M3 6h18" />
                            <path d="M6 6v14h12V6" />
                            <path d="M8 6V4h8v2" />
                            <path d="M9 10v6" />
                            <path d="M12 10v6" />
                            <path d="M15 10v6" />
                        </svg>
                    </div>

                    <h2 className="db-empty-title">
                        No Settlement Data Selected
                    </h2>

                    <p className="db-empty-text">
                        Please select your filters and click{" "}
                        <strong>View Settlement Data</strong> to see the settlement details.
                    </p>
                </div>
            )}

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

                .db-root {
                font-family: 'Plus Jakarta Sans', sans-serif;
                min-height: 100vh;
                display: flex; flex-direction: column; gap: 20px;
                }
                
                .db-pill--custom {
                    cursor: not-allowed !important;
                    opacity: 0.6;
                    background: #f1f5f9;
                    border-color: #cbd5e1;
                    color: #64748b;
                }

                .txn-card-type img {
                    display: block;
                    max-width: 42px !important;
                    max-height: 20px !important;
                    width: auto;
                    height: auto;
                    object-fit: contain;
                }
                    
                /* ── Filter card ── */
                .db-filter-card {
                background: #ffffff;
                border-radius: 18px;
                padding: 22px 28px;
                border: 1px solid #e2e8f0;
                box-shadow: 0 2px 8px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.04);
                }

                .db-payout-card{
                    position:relative;
                    overflow:hidden;
                    background:#fff;
                    border:1px solid #dbe4ef;
                    border-radius:18px;
                    box-shadow:0 4px 14px rgba(15,23,42,.05),0 1px 3px rgba(15,23,42,.04);
                }
                .db-payout-card:before{
                    content:"";
                    position:absolute;
                    top:0;
                    left:0;
                    right:0;
                    height:3px;
                    background:linear-gradient(90deg,#2563eb,#7c3aed,#06b6d4);
                }
                .db-payout-header{
                    display:flex;
                    align-items:center;
                    justify-content:space-between;
                    gap:16px;
                    padding:19px 21px;
                    border-bottom:1px solid #eef2f7;
                }
                .db-payout-title-wrap{
                    display:flex;
                    align-items:center;
                    gap:12px;
                }
                .db-payout-icon{
                    width:40px;
                    height:40px;
                    border-radius:11px;
                    display:flex;
                    align-items:center;
                    justify-content:center;
                    color:#2563eb;
                    background:linear-gradient(135deg,#eff6ff,#dbeafe);
                    border:1px solid #bfdbfe;
                    flex-shrink:0;
                }
                .db-payout-header h3{
                    margin:0;
                    color:#0f172a;
                    font-size:14px;
                    font-weight:800;
                    letter-spacing:-.2px;
                }
                .db-payout-header p{
                    margin:4px 0 0;
                    color:#94a3b8;
                    font-size:10.5px;
                    font-weight:500;
                }
                .db-payout-badge{
                    display:flex;
                    align-items:center;
                    gap:7px;
                    padding:7px 10px;
                    border-radius:8px;
                    background:#f0fdf4;
                    border:1px solid #dcfce7;
                    color:#15803d;
                    font-size:10px;
                    font-weight:800;
                    white-space:nowrap;
                }
                .db-payout-badge-dot{
                    width:6px;
                    height:6px;
                    border-radius:50%;
                    background:#22c55e;
                    box-shadow:0 0 0 3px #dcfce7;
                }
                .db-payout-body{
                    padding:18px 21px 20px;
                }
                .db-payout-calculation{
                    max-width:900px;
                    margin:0 auto;
                }
                .db-payout-row{
                    min-height:58px;
                    display:flex;
                    align-items:center;
                    justify-content:space-between;
                    gap:20px;
                    padding:8px 14px;
                    border-radius:11px;
                }
                .db-payout-row:hover{
                    background:#f8fafc;
                }
                .db-payout-label{
                    display:flex;
                    align-items:center;
                    gap:11px;
                }
                .db-payout-label > div{
                    display:flex;
                    flex-direction:column;
                    gap:2px;
                }
                .db-payout-label strong{
                    color:#334155;
                    font-size:12px;
                    font-weight:700;
                }
                .db-payout-label small{
                    color:#94a3b8;
                    font-size:9.5px;
                    font-weight:500;
                }
                .db-payout-row-icon{
                    width:29px;
                    height:29px;
                    border-radius:8px;
                    display:flex;
                    align-items:center;
                    justify-content:center;
                    font-size:16px;
                    font-weight:800;
                    flex-shrink:0;
                }
                .db-payout-positive{
                    color:#16a34a;
                    background:#f0fdf4;
                    border:1px solid #dcfce7;
                }
                .db-payout-negative{
                    color:#dc2626;
                    background:#fef2f2;
                    border:1px solid #fee2e2;
                }
                .db-payout-reserve{
                    color:#7c3aed;
                    background:#f5f3ff;
                    border:1px solid #ede9fe;
                }
                .db-payout-value{
                    font-size:13px;
                    font-weight:800;
                    white-space:nowrap;
                }
                .db-value-positive{
                    color:#15803d;
                }
                .db-value-negative{
                    color:#dc2626;
                }
                .db-value-reserve{
                    color:#7c3aed;
                }
                .db-payout-equation{
                    margin:3px 14px 5px;
                }
                .db-payout-equation-line{
                    height:1px;
                    background:#cbd5e1;
                    margin-bottom:2px;
                }
                .db-payout-subtotal{
                    display:flex;
                    align-items:center;
                    justify-content:space-between;
                    gap:20px;
                    padding:12px 0 13px;
                }
                .db-payout-subtotal > div{
                    display:flex;
                    flex-direction:column;
                    gap:3px;
                }
                .db-payout-subtotal span{
                    color:#475569;
                    font-size:11.5px;
                    font-weight:800;
                }
                .db-payout-subtotal small{
                    color:#94a3b8;
                    font-size:9.5px;
                    font-weight:500;
                }
                .db-payout-subtotal strong{
                    color:#0f172a;
                    font-size:16px;
                    font-weight:800;
                    white-space:nowrap;
                }
                .db-payout-reserve-row{
                    margin-top:3px;
                }
                .db-payout-final{
                    margin-top:8px;
                    padding:15px 17px;
                    border-radius:13px;
                    display:flex;
                    align-items:center;
                    justify-content:space-between;
                    gap:20px;
                    background:linear-gradient(135deg,#f0fdf4,#ecfdf5);
                    border:1px solid #bbf7d0;
                    box-shadow:inset 0 1px 0 rgba(255,255,255,.7);
                }
                .db-payout-final-left{
                    display:flex;
                    align-items:center;
                    gap:11px;
                }
                .db-payout-final-icon{
                    width:34px;
                    height:34px;
                    border-radius:9px;
                    display:flex;
                    align-items:center;
                    justify-content:center;
                    color:#15803d;
                    background:#dcfce7;
                    border:1px solid #bbf7d0;
                }
                .db-payout-final-left > div:last-child{
                    display:flex;
                    flex-direction:column;
                    gap:2px;
                }
                .db-payout-final-left span{
                    color:#166534;
                    font-size:12px;
                    font-weight:800;
                }
                .db-payout-final-left small{
                    color:#65a30d;
                    font-size:9.5px;
                    font-weight:500;
                }
                .db-payout-final > strong{
                    color:#15803d;
                    font-size:22px;
                    font-weight:800;
                    letter-spacing:-.5px;
                    white-space:nowrap;
                }
                .db-payout-formula{
                    margin:17px auto 0;
                    max-width:900px;
                    padding:11px 14px;
                    border:1px dashed #dbe4ef;
                    border-radius:10px;
                    background:#f8fafc;
                }
                .db-formula-title{
                    display:flex;
                    align-items:center;
                    gap:6px;
                    margin-bottom:8px;
                    color:#64748b;
                    font-size:9px;
                    font-weight:800;
                    text-transform:uppercase;
                    letter-spacing:.6px;
                }
                .db-formula-content{
                    display:flex;
                    align-items:center;
                    flex-wrap:wrap;
                    gap:7px;
                    color:#64748b;
                    font-size:9.5px;
                    font-weight:600;
                }
                .db-formula-content b{
                    color:#94a3b8;
                    font-size:11px;
                }
                .db-formula-content strong{
                    color:#334155;
                    font-weight:800;
                }
                .db-formula-content .db-formula-final{
                    color:#15803d;
                }
                @media(max-width:700px){
                    .db-payout-header{
                        align-items:flex-start;
                        flex-direction:column;
                    }
                    .db-payout-badge{
                        align-self:flex-start;
                    }
                    .db-payout-body{
                        padding:14px;
                    }
                    .db-payout-row{
                        padding:8px 5px;
                    }
                    .db-payout-label strong{
                        font-size:11px;
                    }
                    .db-payout-label small{
                        font-size:9px;
                    }
                    .db-payout-value{
                        font-size:12px;
                    }
                    .db-payout-final{
                        padding:13px;
                    }
                    .db-payout-final > strong{
                        font-size:18px;
                    }
                }

                /* Top row: title + toggle */
                .db-toprow {
                display: flex; align-items: center; justify-content: space-between;
                gap: 16px; margin-bottom: 18px; flex-wrap: wrap;
                }
                .db-page-title { display: flex; align-items: center; gap: 12px; }
                .db-page-icon {
                width: 40px; height: 40px; border-radius: 11px;
                background: linear-gradient(135deg, #eff6ff, #dbeafe);
                border: 1.5px solid #bfdbfe;
                display: flex; align-items: center; justify-content: center;
                color: #2563eb; flex-shrink: 0;
                }
                .db-heading { font-size: 18px; font-weight: 800; color: #0f172a; letter-spacing: -0.4px; margin-bottom: 1px; line-height:22px}
                .db-subheading { font-size: 12px; color: #94a3b8; font-weight: 500; }

                /* Toggle */
                .db-mode-toggle { display: flex; align-items: center; gap: 10px; cursor: pointer; }
                .db-toggle-inp { display: none; }
                .db-toggle-track {
                width: 46px; height: 26px; border-radius: 100px;
                background: #e2e8f0; position: relative; transition: background 0.22s;
                flex-shrink: 0;
                }
                .db-toggle-inp:checked ~ .db-toggle-track { background: #2563eb; }
                .db-toggle-thumb {
                position: absolute; top: 3px; left: 3px;
                width: 20px; height: 20px; border-radius: 50%;
                background: #fff; transition: transform 0.22s;
                box-shadow: 0 1px 4px rgba(0,0,0,0.18);
                }
                .db-toggle-inp:checked ~ .db-toggle-track .db-toggle-thumb { transform: translateX(20px); }
                .db-mode-labels { display: flex; align-items: center; gap: 4px; }
                .db-mode-sep { font-size: 13px; color: #cbd5e1; }
                .db-mode-lbl { font-size: 13px; font-weight: 600; color: #94a3b8; transition: color 0.18s; }
                .db-mode-lbl--active-test { color: #ea580c; }
                .db-mode-lbl--active-live { color: #16a34a; }

                .db-divider { height: 1px; background: #f1f5f9; margin: 4px 0 18px; }

                .txn-gateway-cell { display:flex; align-items:center; gap:6px; }
                .txn-gateway-name { font-size:12.5px; font-weight:600; color:#0f172a; }

                .db-stats-section{display:flex;flex-direction:column;gap:16px}
                .db-stats-header{display:flex;align-items:center;justify-content:space-between;padding:4px 2px}
                .db-stats-header h2{margin:0;font-size:16px;font-weight:800;color:#0f172a;letter-spacing:-.3px}
                .db-stats-header p{margin:3px 0 0;font-size:11.5px;color:#94a3b8;font-weight:500}
                .db-stats-live{display:flex;align-items:center;gap:7px;padding:7px 11px;border:1px solid #e2e8f0;border-radius:9px;background:#fff;font-size:11px;font-weight:700;color:#64748b}
                .db-stats-live span{width:7px;height:7px;border-radius:50%;background:#22c55e;box-shadow:0 0 0 3px #dcfce7}
                .db-main-stats{display:grid;grid-template-columns:repeat(4,1fr);gap:14px}
                .db-stat-card{position:relative;overflow:hidden;background:#fff;border:1px solid #e2e8f0;border-radius:16px;padding:18px;box-shadow:0 2px 7px rgba(15,23,42,.04);transition:all .2s}
                .db-stat-card:hover{transform:translateY(-2px);box-shadow:0 8px 24px rgba(15,23,42,.08);border-color:#d7e2f0}
                .db-stat-card:after{content:"";position:absolute;right:-30px;bottom:-35px;width:100px;height:100px;border-radius:50%;background:rgba(37,99,235,.035)}
                .db-stat-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:15px}
                .db-stat-icon{width:38px;height:38px;border-radius:11px;display:flex;align-items:center;justify-content:center}
                .db-stat-icon svg{width:19px;height:19px}
                .db-stat-blue{background:#eff6ff;color:#2563eb}
                .db-stat-green{background:#ecfdf5;color:#059669}
                .db-stat-purple{background:#f5f3ff;color:#7c3aed}
                .db-stat-orange{background:#fff7ed;color:#ea580c}
                .db-stat-badge{padding:4px 7px;border-radius:6px;background:#eff6ff;color:#2563eb;font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:.4px}
                .db-badge-green{background:#ecfdf5;color:#059669}
                .db-badge-purple{background:#f5f3ff;color:#7c3aed}
                .db-badge-orange{background:#fff7ed;color:#ea580c}
                .db-stat-value{font-size:24px;font-weight:800;color:#0f172a;letter-spacing:-.7px;line-height:30px}
                .db-stat-label{margin-top:3px;font-size:11px;font-weight:600;color:#94a3b8}
                .db-status-card{background:#fff;border:1px solid #e2e8f0;border-radius:16px;padding:18px 20px;box-shadow:0 2px 7px rgba(15,23,42,.04)}
                .db-status-heading{display:flex;align-items:center;justify-content:space-between;padding-bottom:15px;border-bottom:1px solid #f1f5f9}
                .db-status-heading h3{margin:0;font-size:14px;font-weight:800;color:#0f172a}
                .db-status-heading p{margin:3px 0 0;font-size:10.5px;color:#94a3b8}
                .db-total-volume{display:flex;align-items:center;gap:8px;padding:6px 10px;background:#f8fafc;border-radius:8px}
                .db-total-volume span{font-size:10px;color:#94a3b8;font-weight:600}
                .db-total-volume strong{font-size:13px;color:#334155}
                .db-status-grid{display:grid;grid-template-columns:repeat(5,1fr);gap:0}
                .db-charges-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:0}
                .db-status-item{display:flex;align-items:center;justify-content:space-between;padding:15px 16px 4px 0;margin-right:16px;border-right:1px solid #f1f5f9}
                .db-status-item:last-child{border-right:0;margin-right:0}
                .db-status-left{display:flex;align-items:center;gap:9px;min-width:0}
                .db-status-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0}
                .db-dot-approved{background:#10b981;box-shadow:0 0 0 3px #d1fae5}
                .db-dot-declined{background:#ef4444;box-shadow:0 0 0 3px #fee2e2}
                .db-dot-refunded{background:#8b5cf6;box-shadow:0 0 0 3px #ede9fe}
                .db-dot-errored{background:#f59e0b;box-shadow:0 0 0 3px #fef3c7}
                .db-dot-failed{background:#64748b;box-shadow:0 0 0 3px #e2e8f0}
                .db-status-left div{display:flex;flex-direction:column;gap:2px}
                .db-status-left strong{font-size:11px;color:#334155;font-weight:750}
                .db-status-left small{font-size:9px;color:#a0aec0;white-space:nowrap}
                .db-status-right{display:flex;flex-direction:column;align-items:flex-end;gap:2px}
                .db-status-right strong{font-size:14px;color:#0f172a;font-weight:800}
                .db-status-right span{font-size:9px;color:#94a3b8;font-weight:600}
                @media(max-width:1100px){.db-main-stats{grid-template-columns:repeat(2,1fr)}.db-status-grid{grid-template-columns:repeat(3,1fr)}.db-charges-grid{grid-template-columns:repeat(2,1fr)}.db-status-item:nth-child(3){border-right:0}.db-status-item:nth-child(4),.db-status-item:nth-child(5){margin-top:12px}}
                @media(max-width:768px){.db-main-stats{grid-template-columns:1fr}.db-status-grid{grid-template-columns:1fr}.db-charges-grid{grid-template-columns:1fr}.db-status-item,.db-status-item:nth-child(n){border-right:0;border-bottom:1px solid #f1f5f9;margin:0;padding:12px 0}.db-status-item:last-child{border-bottom:0}.db-stats-header{align-items:flex-start;gap:10px;flex-direction:column}}
                
                .st-card{font-family:'Plus Jakarta Sans',sans-serif;background:#fff;border:1px solid #e2e8f0;border-radius:18px;overflow:hidden;box-shadow:0 2px 8px rgba(15,23,42,.06)}
                .st-header{padding:18px 20px 15px;border-bottom:1px solid #f1f5f9;display:flex;align-items:center;justify-content:space-between}
                .st-header-left{display:flex;align-items:center;gap:11px}
                .st-header-icon{width:36px;height:36px;border-radius:10px;background:linear-gradient(135deg,#eff6ff,#dbeafe);border:1.5px solid #bfdbfe;display:flex;align-items:center;justify-content:center;color:#2563eb}
                .st-title{font-size:14px;font-weight:800;color:#0f172a;letter-spacing:-.2px;margin:0 0 2px;line-height:20px}
                .st-subtitle{font-size:10.5px;color:#94a3b8;font-weight:500;margin:0}
                .st-table-wrap{overflow-x:auto;width:100%}
                .st-table-wrap::-webkit-scrollbar{height:4px}
                .st-table-wrap::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:4px}
                .st-table{width:100%;border-collapse:separate;border-spacing:0;table-layout:auto}
                .st-thead-th{padding:10px 12px 9px;background:#f8fafc;border-bottom:1.5px solid #e2e8f0;text-align:left;white-space:nowrap;user-select:none}
                .st-thead-th--sort{cursor:pointer}
                .st-thead-th--sort:hover{background:#f1f5f9}
                .st-th-inner{display:flex;align-items:center;gap:5px}
                .st-th{font-size:10px;font-weight:800;color:#374151;text-transform:uppercase;letter-spacing:.45px}
                .st-sort-icon{color:#94a3b8;display:flex;align-items:center}
                .st-tbody-tr{transition:background .12s}
                .st-tbody-tr:hover{background:#f8fafc}
                .st-tbody-tr--alt{background:#fafbfc}
                .st-tbody-tr--alt:hover{background:#f1f5f9}
                .st-tbody-td{padding:10px 12px;border-bottom:1px solid #f1f5f9;vertical-align:middle}
                .st-payment-cell{display:flex;flex-direction:column;gap:2px;min-width:135px}
                .st-payment-id{font-size:10.5px;font-weight:700;color:#2563eb;font-family:monospace;white-space:nowrap}
                .st-payment-currency{font-size:9px;color:#94a3b8;font-weight:600;text-transform:uppercase}
                .st-transaction-id{display:block;max-width:145px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:10.5px;color:#475569;font-family:monospace;font-weight:500}
                .st-amount-cell{display:flex;align-items:center;min-width:95px}
                .st-amount{font-size:11.5px;font-weight:800;color:#0f172a;white-space:nowrap}
                .st-reserve{font-size:11px;font-weight:700;color:#7c3aed;white-space:nowrap}
                .st-release-amount{font-size:11px;font-weight:700;color:#059669;white-space:nowrap}
                .st-date{font-size:10.5px;color:#64748b;font-weight:600;white-space:nowrap}
                .st-status{display:inline-flex;align-items:center;gap:5px;padding:4px 8px;border-radius:6px;font-size:9.5px;font-weight:700;white-space:nowrap}
                .st-status-dot{width:5px;height:5px;border-radius:50%}
                .st-status-approved{background:#ecfdf5;border:1px solid #bbf7d0;color:#15803d}
                .st-status-approved .st-status-dot{background:#16a34a}
                .st-status-captured{background:#ecfdf5;border:1px solid #bbf7d0;color:#15803d}
                .st-status-captured .st-status-dot{background:#16a34a}
                .st-status-declined{background:#fef2f2;border:1px solid #fecaca;color:#dc2626}
                .st-status-declined .st-status-dot{background:#ef4444}
                .st-status-refunded{background:#f5f3ff;border:1px solid #ddd6fe;color:#7c3aed}
                .st-status-refunded .st-status-dot{background:#8b5cf6}
                .st-status-errored{background:#fffbeb;border:1px solid #fde68a;color:#b45309}
                .st-status-errored .st-status-dot{background:#f59e0b}
                .st-status-failed{background:#f8fafc;border:1px solid #e2e8f0;color:#64748b}
                .st-status-failed .st-status-dot{background:#64748b}
                .st-release-badge{display:inline-flex;align-items:center;gap:4px;padding:4px 8px;border-radius:6px;font-size:9.5px;font-weight:700;white-space:nowrap}
                .st-release-yes{background:#ecfdf5;border:1px solid #bbf7d0;color:#15803d}
                .st-release-no{background:#f8fafc;border:1px solid #dbe3ef;color:#94a3b8}
                .st-empty{text-align:center;padding:55px 20px}
                .st-empty-icon{width:48px;height:48px;border-radius:13px;background:#eff6ff;border:1px solid #dbeafe;color:#2563eb;display:flex;align-items:center;justify-content:center;margin:0 auto 12px}
                .st-empty-title{font-size:13px;font-weight:700;color:#64748b;margin:0 0 4px}
                .st-empty-sub{font-size:10.5px;color:#94a3b8;margin:0}
                @media(max-width:900px){.st-table{min-width:1050px}}

                .db-loader-wrap{min-height:300px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;background:#fff;border:1px solid #e2e8f0;border-radius:18px;box-shadow:0 2px 8px rgba(15,23,42,.04);font-size:13px;font-weight:600;color:#64748b}

                /* Filter grid */
                .db-filter-grid {
                display: grid;
                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                gap: 16px;
                margin-bottom: 4px;
                }
                .db-filter-group { display: flex; flex-direction: column; gap: 7px; }
                .db-filter-label {
                display: flex; align-items: center; gap: 5px;
                font-size: 11.5px; font-weight: 700; color: #374151;
                letter-spacing: 0.4px; text-transform: uppercase;
                }
                .db-ms { font-family: 'Plus Jakarta Sans', sans-serif !important; }
                .db-daterange { }

                /* Time frame row */
                .db-tf-row { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
                .db-tf-pills { display: flex; flex-wrap: wrap; gap: 6px; }
                .db-pill {
                padding: 6px 14px;
                border-radius: 8px;
                border: 1.5px solid #e2e8f0;
                background: #f8fafc;
                font-family: 'Plus Jakarta Sans', sans-serif;
                font-size: 12.5px; font-weight: 600;
                color: #64748b; cursor: pointer;
                transition: all 0.15s;
                }
                .db-pill:hover { border-color: #93c5fd; color: #2563eb; background: #eff6ff; }
                .db-pill--active { border-color: #2563eb !important; background: #eff6ff !important; color: #1d4ed8 !important; }

                /* Content grid */
                .db-content-grid {
                display: grid;
                grid-template-columns: repeat(2, 1fr);
                gap: 20px;
                }
                .db-span-full { grid-column: 1 / -1; }
                .db-pie-pair { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; grid-column: 1 / -1;align-items: stretch; }

                @media (max-width: 1024px) {
                .db-content-grid { grid-template-columns: 1fr; }
                .db-pie-pair { grid-template-columns: 1fr; }
                }
                @media (max-width: 768px) {
                .db-root { padding: 14px; gap: 14px; }
                .db-filter-card { padding: 18px 16px; }
                .db-filter-grid { grid-template-columns: 1fr; }
                }
                @media (max-width: 480px) {
                .db-root { padding: 10px; gap: 12px; }
                .db-toprow { flex-direction: column; align-items: flex-start; }
                }

                // .db-ad-payments-grid {
                //   display: grid;
                //   gap: 20px;
                //   grid-column: 1 / -1;
                // }

                .db-ad-payments-grid {
                --ad-payment-count: 1;

                display: grid;
                grid-template-columns: repeat(
                    var(--ad-payment-count),
                    minmax(0, 1fr)
                );
                gap: 20px;
                grid-column: 1 / -1;
                }

                /* Tablet */
                @media (max-width: 1200px) {
                .db-ad-payments-grid {
                    grid-template-columns: repeat(
                    min(2, var(--ad-payment-count)),
                    minmax(0, 1fr)
                    );
                }
                }

                .org-add-btn {
                    display: inline-flex; align-items: center; gap: 7px;
                    font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13px; font-weight: 700; color: #fff;
                    padding: 9px 18px; border-radius: 10px; border: none;
                    background: linear-gradient(135deg, #1d4ed8, #2563eb);
                    cursor: pointer; box-shadow: 0 3px 12px rgba(37,99,235,.3);
                    transition: transform .15s, box-shadow .18s;
                }
                .org-add-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(37,99,235,.38); }
                
                .ct-search-icon {
                    font-size: 16px;
                }

                .wz-spinner { display:inline-block;width:16px;height:16px;border:2px solid rgba(255,255,255,.35);border-top-color:#fff;border-radius:50%;animation:wzSpin .65s linear infinite; }
                @keyframes wzSpin { to{transform:rotate(360deg)} }

                .wz-spinner-load { display:inline-block;width:36px;height:36px;border:4px solid #dbeafe;border-top-color:#2563eb;border-radius:50%;animation:wzSpinLoad .7s linear infinite; }
                @keyframes wzSpinLoad { to{transform:rotate(360deg)} }

                .db-empty-state {
                    min-height: 360px;
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 18px;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    text-align: center;
                    padding: 40px 20px;
                    box-shadow: 0 2px 8px rgba(15,23,42,0.04);
                }

                .db-empty-icon {
                    width: 72px;
                    height: 72px;
                    border-radius: 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #eff6ff;
                    border: 1px solid #dbeafe;
                    color: #2563eb;
                    margin-bottom: 18px;
                }

                .db-empty-title {
                    margin: 0 0 8px;
                    font-size: 17px;
                    font-weight: 800;
                    color: #0f172a;
                }

                .db-empty-text {
                    margin: 0;
                    max-width: 480px;
                    font-size: 13px;
                    line-height: 20px;
                    color: #94a3b8;
                }

                .db-empty-text strong {
                    color: #475569;
                    font-weight: 700;
                }

                /* Mobile */
                @media (max-width: 768px) {
                .db-ad-payments-grid {
                    grid-template-columns: 1fr;
                }
                }
            `}</style>
        </div>
    );

};

export default SettlementSummary;